import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Consensus: Proof of History + Tower BFT',
    tagline: 'A verifiable clock, plus a vote: Solana\'s answer to PoW and PoS.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what Proof of History actually provides (a verifiable clock, not consensus itself)
//   Q1 - Tower BFT's role (the actual voting mechanism, built on top of PoH)
//   Q2 - the trade-off Solana's design leans toward, contrasted with PoW/PoS
export const blocks: StageBlock[] = [
    content('poh', () => (
        <StageSection gradientBorder eyebrow="Stage 7 of 8" title="Proof of History: a clock, not a vote">
            <Prose>
                Solana's signature idea is Proof of History (PoH): a sequential hash chain where each output feeds
                back in as the input to the next hash. Because each step depends on the one before it, the number of
                hashes computed is itself a verifiable measure of how much time has passed. This gives every
                validator a shared, cryptographically provable sense of ordering and elapsed time, without needing
                to constantly exchange messages just to agree on what happened first.
            </Prose>
            <FactGrid items={[
                ['What PoH is', 'A verifiable clock: proof that time passed and in what order events occurred.'],
                ['What PoH is not', 'A consensus mechanism by itself; it doesn\'t decide which fork is canonical.'],
            ]} />
        </StageSection>
    )),

    question(0),

    content('tower-bft', () => (
        <StageSection title="Tower BFT: voting on top of that clock">
            <Prose>
                The actual agreement on which chain of blocks is canonical comes from Tower BFT, a voting-based
                consensus algorithm descended from practical Byzantine fault tolerance (PBFT). Validators vote on
                the state of the chain using PoH as their shared clock, and votes on progressively older forks incur
                exponentially increasing "lockout" periods, a built-in incentive that pushes validators to converge
                on one fork quickly rather than hedging between competing ones.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('contrast', () => (
        <StageSection title="The same problem, three different trade-offs">
            <Prose>
                Bitcoin's proof of work secures ordering through expensive, unavoidable computation and roughly
                10-minute blocks. Ethereum's proof of stake secures it through economic stake that can be slashed
                for provable misbehavior. Solana's PoH-plus-Tower-BFT design leans toward speed: fast ordering and
                sub-second confirmation, in exchange for requiring more capable validator hardware and tighter
                time-synchronization assumptions than the other two.
            </Prose>
            <KeyPoints items={[
                <>None of these approaches is simply "better": each optimizes for a different point in the same trade-off space.</>,
                <>One stage left: who actually runs this, and where Anchor fits in.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
